import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Button, Table, Alert, Spinner, Tab, Tabs, Modal, Form } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import programAssignmentService from '../services/programAssignmentService';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';

const FacilitatorProgramView = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [assignedPrograms, setAssignedPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [programStats, setProgramStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showCohortModal, setShowCohortModal] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  // Module form data
  const [moduleFormData, setModuleFormData] = useState({
    title: '',
    description: '',
    content: '',
    duration: '',
    difficulty: 'beginner',
    objectives: '',
    resources: ''
  });

  // Cohort form data
  const [cohortFormData, setCohortFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    capacity: 30,
    status: 'upcoming'
  });

  useEffect(() => {
    if (currentUser && (userProfile?.role === 'facilitator' || userProfile?.role === 'admin')) {
      loadFacilitatorPrograms();
    }
  }, [currentUser, userProfile]);

  const loadFacilitatorPrograms = async () => {
    try {
      setLoading(true);
      const programs = await programAssignmentService.getFacilitatorPrograms();
      setAssignedPrograms(programs);

      // Load stats for the first program if available
      if (programs.length > 0) {
        setSelectedProgram(programs[0]);
        const stats = await programAssignmentService.getProgramStats(programs[0].id);
        setProgramStats({ [programs[0].id]: stats });
      }
    } catch (error) {
      devError('Error loading facilitator programs:', error);
      showAlert('Error loading assigned programs', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const loadProgramStats = async (programId) => {
    try {
      if (!programStats[programId]) {
        const stats = await programAssignmentService.getProgramStats(programId);
        setProgramStats(prev => ({ ...prev, [programId]: stats }));
      }
    } catch (error) {
      devError('Error loading program stats:', error);
      showAlert('Error loading program statistics', 'danger');
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleProgramSelect = async (program) => {
    setSelectedProgram(program);
    await loadProgramStats(program.id);
  };

  const handleShowModuleModal = () => {
    setModuleFormData({
      title: '',
      description: '',
      content: '',
      duration: '',
      difficulty: 'beginner',
      objectives: '',
      resources: ''
    });
    setShowModuleModal(true);
  };

  const handleShowCohortModal = () => {
    setCohortFormData({
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      capacity: 30,
      status: 'upcoming'
    });
    setShowCohortModal(true);
  };

  const handleCreateModule = async (e) => {
    e.preventDefault();

    if (!selectedProgram) {
      showAlert('No program selected', 'danger');
      return;
    }

    try {
      const moduleData = {
        ...moduleFormData,
        programId: selectedProgram.id,
        facilitatorId: currentUser.uid,
        status: 'draft',
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'modules'), moduleData);
      showAlert('Module created successfully!', 'success');
      setShowModuleModal(false);

      // Reload program stats
      await loadProgramStats(selectedProgram.id);
    } catch (error) {
      devError('Error creating module:', error);
      showAlert('Error creating module: ' + error.message, 'danger');
    }
  };

  const handleCreateCohort = async (e) => {
    e.preventDefault();

    if (!selectedProgram) {
      showAlert('No program selected', 'danger');
      return;
    }

    try {
      const cohortData = {
        ...cohortFormData,
        programId: selectedProgram.id,
        facilitatorId: currentUser.uid,
        capacity: parseInt(cohortFormData.capacity),
        enrolledCount: 0,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        updatedAt: serverTimestamp(),
        startDate: new Date(cohortFormData.startDate),
        endDate: new Date(cohortFormData.endDate)
      };

      await addDoc(collection(db, 'cohorts'), cohortData);
      showAlert('Cohort created successfully!', 'success');
      setShowCohortModal(false);

      // Reload program stats
      await loadProgramStats(selectedProgram.id);
    } catch (error) {
      devError('Error creating cohort:', error);
      showAlert('Error creating cohort: ' + error.message, 'danger');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Not set';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  if (userProfile?.role !== 'facilitator' && userProfile?.role !== 'admin') {
    return (
      <Alert variant="danger">
        <h5>Access Denied</h5>
        <p>Only facilitators can view program assignments.</p>
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="text-center my-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading your assigned programs...</p>
      </div>
    );
  }

  if (assignedPrograms.length === 0) {
    return (
      <Alert variant="info">
        <h5>No Programs Assigned</h5>
        <p>You haven't been assigned to any programs yet. Contact your administrator to get program assignments.</p>
      </Alert>
    );
  }

  const currentStats = selectedProgram ? programStats[selectedProgram.id] : null;

  return (
    <>
      {alert.show && (
        <Alert variant={alert.type} className="mb-3">
          {alert.message}
        </Alert>
      )}

      {/* Program Overview */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-primary">{assignedPrograms.length}</h4>
              <small className="text-muted">Assigned Programs</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-success">{currentStats?.totalModules || 0}</h4>
              <small className="text-muted">Total Modules</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-info">{currentStats?.totalCohorts || 0}</h4>
              <small className="text-muted">Total Cohorts</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-warning">{currentStats?.totalLearners || 0}</h4>
              <small className="text-muted">Total Learners</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Program Selection */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <h5>Your Assigned Programs</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex flex-wrap gap-2">
                {assignedPrograms.map((program) => (
                  <Button
                    key={program.id}
                    variant={selectedProgram?.id === program.id ? "primary" : "outline-primary"}
                    onClick={() => handleProgramSelect(program)}
                    className="mb-2"
                  >
                    {program.name}
                    <Badge bg="secondary" className="ms-2">{program.level}</Badge>
                  </Button>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Selected Program Details */}
      {selectedProgram && (
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5>{selectedProgram.name}</h5>
            <div>
              <Button variant="success" size="sm" className="me-2" onClick={handleShowModuleModal}>
                Create Module
              </Button>
              <Button variant="info" size="sm" onClick={handleShowCohortModal}>
                Create Cohort
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={8}>
                <h6>Program Details</h6>
                <p><strong>Description:</strong> {selectedProgram.description || 'No description provided'}</p>
                <p><strong>Category:</strong> <Badge bg="info">{selectedProgram.category}</Badge></p>
                <p><strong>Level:</strong> <Badge bg="secondary">{selectedProgram.level}</Badge></p>
                <p><strong>Duration:</strong> {selectedProgram.duration || 'Not specified'}</p>
              </Col>
              <Col md={4}>
                <h6>Program Statistics</h6>
                <p><strong>Active Modules:</strong> {currentStats?.activeModules || 0}</p>
                <p><strong>Active Cohorts:</strong> {currentStats?.activeCohorts || 0}</p>
                <p><strong>Capacity:</strong> {selectedProgram.capacity || 'Not set'}</p>
              </Col>
            </Row>

            <Tabs defaultActiveKey="modules" className="mt-3">
              <Tab eventKey="modules" title={`Modules (${currentStats?.totalModules || 0})`}>
                {currentStats?.modules?.length > 0 ? (
                  <Table striped bordered hover responsive className="mt-3">
                    <thead>
                      <tr>
                        <th>Module Title</th>
                        <th>Status</th>
                        <th>Difficulty</th>
                        <th>Duration</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentStats.modules.map((module) => (
                        <tr key={module.id}>
                          <td>
                            <strong>{module.title}</strong>
                            {module.description && (
                              <div><small className="text-muted">{module.description}</small></div>
                            )}
                          </td>
                          <td>
                            <Badge bg={module.status === 'active' ? 'success' : module.status === 'draft' ? 'warning' : 'secondary'}>
                              {module.status}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg="info">{module.difficulty}</Badge>
                          </td>
                          <td>{module.duration || 'Not set'}</td>
                          <td>{formatDate(module.createdAt)}</td>
                          <td>
                            <Button variant="outline-primary" size="sm" onClick={() => navigate(`/module/${module.id}`)}>
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">No modules created yet.</p>
                    <Button variant="primary" onClick={handleShowModuleModal}>
                      Create Your First Module
                    </Button>
                  </div>
                )}
              </Tab>

              <Tab eventKey="cohorts" title={`Cohorts (${currentStats?.totalCohorts || 0})`}>
                {currentStats?.cohorts?.length > 0 ? (
                  <Table striped bordered hover responsive className="mt-3">
                    <thead>
                      <tr>
                        <th>Cohort Name</th>
                        <th>Status</th>
                        <th>Enrolled</th>
                        <th>Capacity</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentStats.cohorts.map((cohort) => (
                        <tr key={cohort.id}>
                          <td>
                            <strong>{cohort.name}</strong>
                            {cohort.description && (
                              <div><small className="text-muted">{cohort.description}</small></div>
                            )}
                          </td>
                          <td>
                            <Badge bg={cohort.status === 'active' ? 'success' : cohort.status === 'upcoming' ? 'warning' : 'secondary'}>
                              {cohort.status}
                            </Badge>
                          </td>
                          <td>{cohort.enrolledCount || 0}</td>
                          <td>{cohort.capacity || 0}</td>
                          <td>{formatDate(cohort.startDate)}</td>
                          <td>{formatDate(cohort.endDate)}</td>
                          <td>
                            <Button variant="outline-primary" size="sm">
                              Manage
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">No cohorts created yet.</p>
                    <Button variant="primary" onClick={handleShowCohortModal}>
                      Create Your First Cohort
                    </Button>
                  </div>
                )}
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      )}

      {/* Create Module Modal */}
      <Modal show={showModuleModal} onHide={() => setShowModuleModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Module - {selectedProgram?.name}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateModule}>
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Module Title *</Form.Label>
                  <Form.Control
                    type="text"
                    value={moduleFormData.title}
                    onChange={(e) => setModuleFormData({...moduleFormData, title: e.target.value})}
                    required
                    placeholder="e.g., Introduction to Web Development"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Difficulty *</Form.Label>
                  <Form.Select
                    value={moduleFormData.difficulty}
                    onChange={(e) => setModuleFormData({...moduleFormData, difficulty: e.target.value})}
                    required
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={moduleFormData.description}
                onChange={(e) => setModuleFormData({...moduleFormData, description: e.target.value})}
                placeholder="Brief description of the module"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Duration</Form.Label>
                  <Form.Control
                    type="text"
                    value={moduleFormData.duration}
                    onChange={(e) => setModuleFormData({...moduleFormData, duration: e.target.value})}
                    placeholder="e.g., 2 hours, 1 week"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Learning Objectives</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={moduleFormData.objectives}
                onChange={(e) => setModuleFormData({...moduleFormData, objectives: e.target.value})}
                placeholder="What will learners achieve after completing this module?"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Module Content</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={moduleFormData.content}
                onChange={(e) => setModuleFormData({...moduleFormData, content: e.target.value})}
                placeholder="Enter the module content, activities, and instructions"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModuleModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create Module
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Create Cohort Modal */}
      <Modal show={showCohortModal} onHide={() => setShowCohortModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Cohort - {selectedProgram?.name}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateCohort}>
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Cohort Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={cohortFormData.name}
                    onChange={(e) => setCohortFormData({...cohortFormData, name: e.target.value})}
                    required
                    placeholder="e.g., Spring 2024 Cohort"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status *</Form.Label>
                  <Form.Select
                    value={cohortFormData.status}
                    onChange={(e) => setCohortFormData({...cohortFormData, status: e.target.value})}
                    required
                  >
                    <option value="upcoming">Upcoming</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={cohortFormData.description}
                onChange={(e) => setCohortFormData({...cohortFormData, description: e.target.value})}
                placeholder="Brief description of the cohort"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={cohortFormData.startDate}
                    onChange={(e) => setCohortFormData({...cohortFormData, startDate: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={cohortFormData.endDate}
                    onChange={(e) => setCohortFormData({...cohortFormData, endDate: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Capacity *</Form.Label>
                  <Form.Control
                    type="number"
                    value={cohortFormData.capacity}
                    onChange={(e) => setCohortFormData({...cohortFormData, capacity: e.target.value})}
                    min="1"
                    max="200"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCohortModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create Cohort
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default FacilitatorProgramView;